import {
  Badge,
  Button2,
  DataGrid,
  DataGridCell,
  DataGridColumnSpec,
  Marquee,
  Text2,
} from '@el8/vital';
import { useCreateAiFeedbackV2V2FeedbackPost } from '@elation/api-client/src/ai-feedback-api/fastAPI';
import { AIFeedbackRatingType } from '@elation/api-client/src/ai-feedback-api/models/aIFeedbackRatingType';
import { DeletedPatient } from 'PracticeDeletedPatientsTypes';
import {
  usePracticeDeletedPatients,
  useRestoreDeletedPatient,
  useUnmergePatient,
} from 'modules/practice-deleted-patients';
import React, { useCallback, useMemo, useState } from 'react';
import alert from 'utils/alert';
import { logEvent } from 'utils/amplitude';
import { RestoreDeletedPatientFeedbackDialog } from './RestoreDeletedPatientFeedbackDialog';
import { RestoreDeletedPatientsDialogUnavailableContent } from './RestoreDeletedPatientsDialogUnavailableContent';
import styles from './RestoreDeletedPatientsDialogContent.less';

const strikethroughStyle: React.CSSProperties = { textDecoration: 'line-through' };

const createColumns: (props: {
  openConfirmation: (patient: DeletedPatient) => void;
  restoredIds: Set<number>;
}) => DataGridColumnSpec<DeletedPatient, string>[] = ({ openConfirmation, restoredIds }) => [
  {
    key: 'patient',
    title: 'Patient',
    valueGetter: (item) => `${item.lastName}, ${item.firstName}`,
    renderCell: ({ props, item }): JSX.Element => {
      const isRestored = restoredIds.has(item.id);
      return (
        <DataGridCell {...props}>
          <div>
            <Text2 tag="strong">
              {isRestored ? (
                <a href={`/patient/${item.id}/`} target="_blank" rel="noopener noreferrer">
                  {item.lastName}, {item.firstName}
                </a>
              ) : (
                <>
                  {item.lastName}, {item.firstName}
                </>
              )}
            </Text2>
            {item.dob && (
              <Text2 tag="div" sizing="sm" appearance="muted">
                DOB: {item.dob}
              </Text2>
            )}
          </div>
        </DataGridCell>
      );
    },
    width: 200,
    sortable: true,
  },
  {
    key: 'deleted',
    title: 'Deleted',
    valueGetter: (item) => new Date(item.delete_date).getTime(),
    renderCell: ({ props, item }): JSX.Element => {
      const isRestored = restoredIds.has(item.id);
      return (
        <DataGridCell {...props}>
          <div style={isRestored ? strikethroughStyle : undefined}>
            <Text2 tag="div">{item.delete_date}</Text2>
            <Text2 tag="div" sizing="sm" appearance="muted">
              by {item.deleted_by}
            </Text2>
          </div>
        </DataGridCell>
      );
    },
    width: 180,
    sortable: true,
  },
  {
    key: 'reason',
    title: 'Method',
    valueGetter: (item) =>
      item.is_merge && item.merged_into ? `Merged into ${item.merged_into.name}` : 'Deleted',
    renderCell: ({ props, item }): JSX.Element => {
      const isRestored = restoredIds.has(item.id);
      return (
        <DataGridCell {...props}>
          {item.is_merge && item.merged_into ? (
            <Text2 style={isRestored ? strikethroughStyle : undefined}>
              Merged into {item.merged_into.name}
            </Text2>
          ) : (
            <Text2 style={isRestored ? strikethroughStyle : undefined}>Deleted</Text2>
          )}
        </DataGridCell>
      );
    },
    width: 220,
  },
  {
    key: 'action',
    title: '',
    valueGetter: (item) => item.id,
    renderCell: ({ props, item }): JSX.Element => {
      const isRestored = restoredIds.has(item.id);
      return (
        <DataGridCell {...props}>
          {isRestored ? (
            <Badge intent="success" label="Restored" />
          ) : (
            <Button2
              label={item.is_merge ? 'Undo Merge' : 'Restore'}
              icon="undo"
              sizing="sm"
              onClick={(): void => {
                openConfirmation(item);
              }}
              data-id={item.id}
            />
          )}
        </DataGridCell>
      );
    },
  },
];

export const RestoreDeletedPatientsDialogContent = (): JSX.Element | null => {
  const { data: deletedPatients = [], isLoading, isError } = usePracticeDeletedPatients();

  const { mutateAsync: restoreDeletedPatient } = useRestoreDeletedPatient();
  const { mutateAsync: unmergePatient } = useUnmergePatient();
  const { mutateAsync: createFeedback } = useCreateAiFeedbackV2V2FeedbackPost();

  const [confirmationTarget, setConfirmationTarget] = useState<{
    patient: DeletedPatient;
    patientName: string;
  } | null>(null);

  const [restoredIds, setRestoredIds] = useState<Set<number>>(new Set());

  const unavailable = isLoading || deletedPatients.length === 0 || isError;

  const openConfirmation = useCallback((patient: DeletedPatient) => {
    setConfirmationTarget({
      patient,
      patientName: `${patient.firstName} ${patient.lastName}`,
    });
  }, []);

  const submitFeedback = useCallback(
    async (patientId: number, reason: string) => {
      try {
        await createFeedback({
          data: {
            practice_id: el8Globals.PRACTICE_ID,
            feature: 'restore_deleted_patient',
            reference_id: String(patientId),
            rating_type: AIFeedbackRatingType.thumbs,
            rating: 'thumbs_down',
            comment: reason || null,
          },
        });
      } catch {
        // Feedback submission failing silently is acceptable — the restore still proceeds.
      }
    },
    [createFeedback],
  );

  const doRestore = useCallback(
    async (patient: DeletedPatient, reason: string, openChart: boolean) => {
      submitFeedback(patient.id, reason);

      try {
        if (patient.is_merge) {
          await unmergePatient({ patientId: patient.id });
          logEvent('Undo Merge Deleted Patient', { patientId: patient.id });
        } else {
          await restoreDeletedPatient({ patientId: patient.id });
          logEvent('Restore Deleted Patient', { patientId: patient.id });
        }

        setRestoredIds((prev) => new Set(prev).add(patient.id));

        if (openChart) {
          window.open(`/patient/${patient.id}/`, '_blank');
        }
      } catch {
        alert(
          patient.is_merge
            ? 'Unable to undo merge. Please contact Elation Support if the issue persists.'
            : 'Unable to restore patient. Please contact Elation Support if the issue persists.',
        );
      }
    },
    [restoreDeletedPatient, submitFeedback, unmergePatient],
  );

  const handleConfirm = useCallback(
    async (reason: string) => {
      if (!confirmationTarget) return;
      setConfirmationTarget(null);
      await doRestore(confirmationTarget.patient, reason, false);
    },
    [confirmationTarget, doRestore],
  );

  const handleConfirmAndOpen = useCallback(
    async (reason: string) => {
      if (!confirmationTarget) return;
      setConfirmationTarget(null);
      await doRestore(confirmationTarget.patient, reason, true);
    },
    [confirmationTarget, doRestore],
  );

  const handleCancel = useCallback(() => {
    if (!confirmationTarget) return;
    const { patient } = confirmationTarget;
    logEvent(
      patient.is_merge ? 'Undo Merge Patient Cancelled' : 'Restore Deleted Patient Cancelled',
      {
        patientId: patient.id,
      },
    );
    setConfirmationTarget(null);
  }, [confirmationTarget]);

  const columns = useMemo(() => {
    return createColumns({ openConfirmation, restoredIds });
  }, [openConfirmation, restoredIds]);

  return (
    <>
      {confirmationTarget && (
        <RestoreDeletedPatientFeedbackDialog
          patientName={confirmationTarget.patientName}
          onConfirm={handleConfirm}
          onConfirmAndOpen={handleConfirmAndOpen}
          onCancel={handleCancel}
        />
      )}
      <Marquee kind="warning" intent="alert" icon="warning" className={styles.marquee}>
        Restored charts require re-invitation to Patient Passport and may lose links across
        integrated systems. After restoration, check your connections.
      </Marquee>
      <DataGrid<DeletedPatient>
        keyExtractor={(patient): number => patient.id}
        data={deletedPatients}
        columns={columns}
        appearance="list"
        density="compact"
        className={styles.datagrid}
        unavailableContent={
          unavailable ? (
            <RestoreDeletedPatientsDialogUnavailableContent
              loading={isLoading}
              empty={deletedPatients.length === 0}
              error={isError}
            />
          ) : undefined
        }
        data-testid="restore-deleted-patients-datagrid"
      />
    </>
  );
};
